import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    FormControl,
    FormControlLabel,
    InputLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import useGetAssociatedInvoices from '../../hooks/useGetAssociatedInvoices';
import usePatchCreditMemoMode from '../../hooks/ProcessedTimesheets/usePatchCreditMemoMode';
import { openInvoicePdf } from '../../Services/InvoiceService';
import { COLORS } from '../../../utils/Application_Constants';
import PtsDetailCalculationErrorsDisplay from './PtsDetailCalculationErrorsDisplay';
import useValidatePtsDetailCalculations from '../../hooks/useValidatePtsDetailCalculations';

const CreditMemoMode = {
    PARTIAL: 'Partial',
    FULL: 'Full',
};

const getPreviousCreditMemoModeMessage = (
    previousCreditMemoMode
) => `A credit memo option has already been selected for the associated
                                invoice. That selection (${previousCreditMemoMode} Credit) will be
                                applied.`;

function InvoiceLink({ invoice, onClick }) {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
        setIsLoading(true);
        await onClick();
        setIsLoading(false);
    };

    return (
        <Typography
            variant="body2"
            component="span"
            sx={{
                textDecoration: 'underline',
                color: COLORS.LT_JOURNEY,
                display: 'inline',
                cursor: 'pointer',
                pointerEvents: isLoading ? 'none' : 'auto',
            }}
            onClick={isLoading ? null : () => handleClick()}
        >
            {invoice.invoiceNumber}
        </Typography>
    );
}

function InvoiceReviewResolutionForm({
    id,
    onClose,
    onSuccess,
    previousCreditMemoMode,
    processedTimesheetEntries = [],
    locations = [],
    onCalculationErrorsChange = () => {},
}) {
    const [creditMemoMode, setCreditMemoMode] = useState(null);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [creditMemoModeAvailabilityAlert, setCreditMemoModeAvailabilityAlert] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const queryClient = useQueryClient();
    const { calculationErrors, isValidatingCalculations, validationError, validateCalculations } =
        useValidatePtsDetailCalculations({
            processedTimesheetId: id,
            processedTimesheetEntries,
            locations,
            onCalculationErrorsChange,
        });
    const {
        isLoading: associatedInvoicesLoading,
        data: associatedInvoicesData,
        error: associatedInvoicesError,
    } = useGetAssociatedInvoices({ id });

    const {
        isFullAvailable,
        isPartialAvailable,
        fullNotAvailableMessage,
        partialNotAvailableMessage,
    } = associatedInvoicesData?.creditMemoModeAvailability || {
        isFullAvailable: true,
        isPartialAvailable: true,
    };
    useEffect(() => {
        // If there is an error getting the associated invoices, or no credit memo options are available show an error alert
        if (associatedInvoicesLoading) {
            if (creditMemoModeAvailabilityAlert) setCreditMemoModeAvailabilityAlert(null);
            setCreditMemoMode(null);
            return;
        }
        if (associatedInvoicesError || (!isFullAvailable && !isPartialAvailable)) {
            setCreditMemoModeAvailabilityAlert({
                message: associatedInvoicesError
                    ? 'There was an error getting the associated invoices.'
                    : 'No credit memo options are available for this processed timesheet.',
                severity: 'error',
            });
            return;
        }
        // creditMemoModeAvailability from associatedInvoicesData takes precedence over previousCreditMemoMode
        // If the partial credit memo is available and the full credit memo is not available, or the previous credit memo mode is partial, set the credit memo mode to partial
        if (
            isPartialAvailable &&
            (!isFullAvailable ||
                previousCreditMemoMode?.toLowerCase() === CreditMemoMode.PARTIAL.toLowerCase())
        ) {
            setCreditMemoMode(CreditMemoMode.PARTIAL);
            setCreditMemoModeAvailabilityAlert({
                message:
                    (!isFullAvailable && fullNotAvailableMessage) ||
                    getPreviousCreditMemoModeMessage(previousCreditMemoMode),
                severity: 'info',
            });
        }
        // If the full credit memo is available and the partial credit memo is not available, or the previous credit memo mode is full, set the credit memo mode to full
        if (
            isFullAvailable &&
            (!isPartialAvailable ||
                previousCreditMemoMode?.toLowerCase() === CreditMemoMode.FULL.toLowerCase())
        ) {
            setCreditMemoMode(CreditMemoMode.FULL);
            setCreditMemoModeAvailabilityAlert({
                message:
                    (!isPartialAvailable && partialNotAvailableMessage) ||
                    getPreviousCreditMemoModeMessage(previousCreditMemoMode),
                severity: 'info',
            });
        }
    }, [
        associatedInvoicesLoading,
        associatedInvoicesError,
        isFullAvailable,
        isPartialAvailable,
        fullNotAvailableMessage,
        partialNotAvailableMessage,
        previousCreditMemoMode,
    ]);

    const handleOpenPdf = async (invoiceId) => {
        try {
            setIsPdfLoading(true);
            await openInvoicePdf(invoiceId);
        } catch (error) {
            console.error('Error opening PDF:', error);
        } finally {
            setIsPdfLoading(false);
        }
    };
    const invoices = associatedInvoicesData?.associatedInvoices || [];

    const { mutate: patchCreditMemoMode, isLoading: isPatchLoading } = usePatchCreditMemoMode({
        onSuccess: () => {
            onSuccess('Resolved');

            // Invalidate processed timesheets lists, processed timesheet details, and activity feed queryClient.invalidateQueries({ queryKey: ['processedTimesheets'] });
            queryClient.invalidateQueries({
                queryKey: ['processedTimesheetDetails', { processedTimesheetId: id.toString() }],
            });
            queryClient.invalidateQueries({
                queryKey: ['getActivityFeed', { processedTimesheetId: id.toString() }],
            });
        },
        onError: (error) => {
            console.error('Error patching credit memo mode:', error);
            setSubmitError('There was an error submitting your request. Please try again.');
        },
    });

    const handleSubmit = async () => {
        setSubmitError(null);

        const isValid = await validateCalculations();

        if (!isValid) {
            return;
        }

        patchCreditMemoMode({ processedTimesheetId: id, creditMemoMode });
    };

    const isDisabled = !!previousCreditMemoMode || (!isFullAvailable && !isPartialAvailable);
    const isPartialDisabled = isDisabled || !isPartialAvailable;
    const isFullDisabled = isDisabled || !isFullAvailable;

    return (
        <Box display="flex" flexDirection="column">
            <Typography variant="h5" sx={{ mb: 3 }}>
                Processing Alert
            </Typography>
            {!associatedInvoicesLoading && associatedInvoicesData ? (
                <>
                    {associatedInvoicesData?.associatedInvoices.length === 0 ? (
                        <Typography variant="body2">
                            This PTS is not linked to any invoices.
                        </Typography>
                    ) : (
                        <Typography variant="body2">
                            This PTS is linked to these invoices:{' '}
                            {invoices.length > 0 &&
                                invoices.map((invoice) => (
                                    <Box
                                        key={invoice.id}
                                        component="span"
                                        sx={{ '&:not(:last-child)::after': { content: '", "' } }}
                                    >
                                        <InvoiceLink
                                            invoice={invoice}
                                            onClick={() => handleOpenPdf(invoice.id)}
                                        />
                                    </Box>
                                ))}
                        </Typography>
                    )}
                    <FormControl sx={{ mt: 3 }}>
                        <InputLabel
                            shrink
                            htmlFor="haveReferenceInTwoYear"
                            sx={{
                                fontSize: '14px',
                                '& .MuiInputLabel-asterisk': {
                                    color: COLORS.LT_PEPPER,
                                },
                            }}
                            required
                        >
                            Select what type of credit you need to do
                        </InputLabel>
                        <div>
                            <RadioGroup
                                row
                                aria-label="completed"
                                name="creditMemoMode"
                                value={creditMemoMode}
                                onChange={(e) => setCreditMemoMode(e.target.value)}
                            >
                                <FormControlLabel
                                    value={CreditMemoMode.PARTIAL}
                                    disabled={isPartialDisabled}
                                    control={
                                        <Radio
                                            color="primary"
                                            style={{ color: COLORS.RAVEN }}
                                            name="creditMemoMode"
                                            disableRipple
                                            size="small"
                                        />
                                    }
                                    label={<span style={{ fontSize: '14px' }}>Partial</span>}
                                />
                                <FormControlLabel
                                    value={CreditMemoMode.FULL}
                                    disabled={isFullDisabled}
                                    control={
                                        <Radio
                                            color="primary"
                                            style={{ color: COLORS.RAVEN }}
                                            name="creditMemoMode"
                                            disableRipple
                                            size="small"
                                        />
                                    }
                                    label={<span style={{ fontSize: '14px' }}>Full Credit</span>}
                                />
                            </RadioGroup>
                        </div>
                        {creditMemoModeAvailabilityAlert && (
                            <Alert severity={creditMemoModeAvailabilityAlert.severity}>
                                {creditMemoModeAvailabilityAlert.message}
                            </Alert>
                        )}
                        <PtsDetailCalculationErrorsDisplay errors={calculationErrors} />
                        <DialogActions sx={{ justifyContent: 'flex-start', padding: 0, mt: 5 }}>
                            <Button
                                disabled={isPatchLoading || isValidatingCalculations}
                                color="primary"
                                onClick={onClose}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                loading={isPatchLoading || isValidatingCalculations}
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={!!associatedInvoicesError || !creditMemoMode}
                            >
                                Confirm
                            </Button>
                        </DialogActions>
                        {submitError && (
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                {submitError}
                            </Typography>
                        )}
                        {validationError && (
                            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                {validationError}
                            </Typography>
                        )}
                    </FormControl>
                    {isPdfLoading && (
                        <Box sx={{ position: 'absolute', bottom: 20, right: 20 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </>
            ) : (
                <Box sx={{ py: 2, w: '100%', textAlign: 'center' }}>
                    <CircularProgress sx={{ margin: 'auto' }} />
                </Box>
            )}
        </Box>
    );
}

export default InvoiceReviewResolutionForm;
